import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { money } from './drawerFormat.js';
import VarianceEvidencePicker from './VarianceEvidencePicker.jsx';
import {
  VARIANCE_ACTIONS,
  VARIANCE_ACTION_LABELS,
} from './varianceActions.js';
import {
  EMPTY_ARRAY,
  accountOptionsFromAppsheet,
  buildVarianceActionPayload,
  bindingFieldsFromContext,
  catalogOptionLabel,
  catalogOptions,
  catalogPrice,
  defaultFixForAction,
  defaultProblemForAction,
  evidenceOverrideFromContext,
  hasSafeAccountBinding,
  isPopcornParentProductCode,
  mergeVarianceActionPrefill,
  normalizeCode,
  preferredAccountForProduct,
} from './varianceActionDialogModel.js';
import { inheritedCpqOverrideFromContext } from './varianceActionEvidenceSnapshot.js';
import { useBillingReconciliationNoteTemplates } from '../../hooks/api/useBillingReconciliationHooks.js';
import { FALLBACK_PRODUCT_NOTE_TEMPLATES } from './productInvestigationNoteTemplates.js';
import { productFamilyKey } from './DrawerEvidenceHelpers.jsx';

const VarianceActionDialog = ({
  draft,
  currentUserName,
  onClose,
  onSave,
  gmItems = EMPTY_ARRAY,
  cpqItems = EMPTY_ARRAY,
  dpeItems = EMPTY_ARRAY,
  appsheetData = null,
  appSheetAccounts = null,
  catalogItems = EMPTY_ARRAY,
}) => {
  const actionType = draft?.actionType;
  const context = draft?.context;
  const [problem, setProblem] = useState('');
  const [fix, setFix] = useState('');
  const [note, setNote] = useState('');
  const [replacementProductCode, setReplacementProductCode] = useState('');
  const [proposedPrice, setProposedPrice] = useState('');
  const [customProductCode, setCustomProductCode] = useState('');
  const [customPrice, setCustomPrice] = useState('');
  const [manualOrderItemId, setManualOrderItemId] = useState('');
  const [manualReferenceId, setManualReferenceId] = useState('');
  const [salesforceAccountId, setSalesforceAccountId] = useState('');
  const [salesforceAccountName, setSalesforceAccountName] = useState('');
  const [manualAccountSelected, setManualAccountSelected] = useState(false);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [selectedCatalogItem, setSelectedCatalogItem] = useState(null);
  const [showAllCatalogProducts, setShowAllCatalogProducts] = useState(false);
  const [customTargetFile, setCustomTargetFile] = useState('gm');
  const [customMutation, setCustomMutation] = useState('adjust');
  const [addToBillingHold, setAddToBillingHold] = useState(false);
  const [clearInheritedEvidence, setClearInheritedEvidence] = useState(false);
  const catalogRows = useMemo(() => catalogOptions(catalogItems), [catalogItems]);
  const contextProductFamily = useMemo(() => productFamilyKey(context?.productCode || ''), [context?.productCode]);
  const replacementCatalogRows = useMemo(() => {
    if (showAllCatalogProducts || !contextProductFamily || contextProductFamily === 'UNCODED') return catalogRows;
    return catalogRows.filter((item) => productFamilyKey(item.productCode) === contextProductFamily);
  }, [catalogRows, contextProductFamily, showAllCatalogProducts]);
  const accountOptions = useMemo(() => accountOptionsFromAppsheet(appsheetData, appSheetAccounts), [appsheetData, appSheetAccounts]);
  const noteTemplatesQuery = useBillingReconciliationNoteTemplates(
    { scope: 'product_investigation', active: true, limit: 200 },
    { enabled: Boolean(draft) },
  );
  const productNoteTemplates = noteTemplatesQuery.isSuccess && noteTemplatesQuery.data?.length
    ? noteTemplatesQuery.data
    : FALLBACK_PRODUCT_NOTE_TEMPLATES;
  const selectedEvidenceHasAccount = Boolean(
    selectedEvidence?.salesforceAccountId
    || selectedEvidence?.row?.accountId
    || selectedEvidence?.row?.salesforceAccountId
    || selectedEvidence?.row?.accountMatch?.salesforceAccountId,
  );
  const requiresManualAccount = accountOptions.length > 1
    && !hasSafeAccountBinding(context)
    && !selectedEvidenceHasAccount
    && !salesforceAccountId
    && !manualAccountSelected;
  const selectedAccountOption = accountOptions.find((account) => (
    account.id === salesforceAccountId
    || account.salesforceAccountId === salesforceAccountId
    || (salesforceAccountName && account.salesforceAccountName === salesforceAccountName)
  ));
  const showAccountPicker = accountOptions.length > 0
    && (accountOptions.length > 1 || requiresManualAccount || !salesforceAccountId);
  const inheritedCpqOverride = useMemo(() => inheritedCpqOverrideFromContext(context), [context]);
  const canClearInheritedEvidence = actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ
    && inheritedCpqOverride.hasOverride;
  const effectiveProductCode = clearInheritedEvidence && canClearInheritedEvidence
    ? inheritedCpqOverride.originalProductCode
    : context?.productCode;
  const canSave = Boolean(problem.trim() || fix.trim() || note.trim())
    && !requiresManualAccount
    && (
      actionType !== VARIANCE_ACTIONS.CUSTOM
      || (['gm', 'cpq'].includes(customTargetFile) && ['add', 'remove', 'adjust'].includes(customMutation))
    );

  useEffect(() => {
    if (!draft) return;
    const prefill = draft.prefillAction || {};
    const evidenceOverride = evidenceOverrideFromContext(draft.context);
    const draftBindingFields = bindingFieldsFromContext(draft.context);
    setProblem(prefill.problem || defaultProblemForAction(draft.actionType));
    setFix(prefill.fix || defaultFixForAction(draft.actionType));
    setNote(prefill.note || '');
    setReplacementProductCode(prefill.replacementProductCode || draft.context?.replacementProductCode || '');
    setProposedPrice(prefill.proposedPrice ?? draft.context?.proposedPrice ?? '');
    setCustomProductCode(prefill.customProductCode || draft.context?.productCode || '');
    setCustomPrice(prefill.customPrice ?? draft.context?.price ?? '');
    setManualOrderItemId(prefill.manualOrderItemId || evidenceOverride.orderItemId);
    setManualReferenceId(prefill.manualReferenceId || evidenceOverride.referenceId);
    const preferredAccount = preferredAccountForProduct({
      productCode: draft.context?.productCode,
      accountOptions,
      currentAccountId: prefill.salesforceAccountId || draftBindingFields.salesforceAccountId,
      currentAccountName: prefill.salesforceAccountName || draftBindingFields.salesforceAccountName,
    });
    const shouldBlockChildDefault = isPopcornParentProductCode(draft.context?.productCode);
    setSalesforceAccountId(preferredAccount?.salesforceAccountId || (shouldBlockChildDefault ? '' : prefill.salesforceAccountId || draftBindingFields.salesforceAccountId));
    setSalesforceAccountName(preferredAccount?.salesforceAccountName || (shouldBlockChildDefault ? '' : prefill.salesforceAccountName || draftBindingFields.salesforceAccountName));
    setManualAccountSelected(false);
    setSelectedEvidence(null);
    setSelectedCatalogItem(null);
    setShowAllCatalogProducts(false);
    setCustomTargetFile(prefill.targetSource || (draft.context?.source === 'cpq' ? 'cpq' : 'gm'));
    setCustomMutation('adjust');
    setAddToBillingHold(
      draft.actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ
        ? prefill.metadata?.addToBillingHold !== false
        : false,
    );
    setClearInheritedEvidence(Boolean(draft.initialClearInheritedEvidence || prefill.metadata?.clearedInheritedEvidence));
  }, [accountOptions, draft]);

  const productSummary = useMemo(() => {
    if (!context) return '';
    const code = effectiveProductCode ? ` - ${effectiveProductCode}` : '';
    const price = context.price || context.price === 0 ? ` - ${money(context.price)}` : '';
    return `${context.productName || 'Product'}${code}${price}`;
  }, [context, effectiveProductCode]);

  const handleSave = () => {
    if (!draft || !context) return;
    const selectedAccount = manualAccountSelected ? selectedAccountOption : null;
    const payload = buildVarianceActionPayload({
      draft,
      context,
      currentUserName,
      selectedEvidence,
      selectedAccount,
      selectedCatalogItem,
      form: {
        problem,
        fix,
        note,
        replacementProductCode,
        proposedPrice,
        customProductCode,
        customPrice,
        manualOrderItemId,
        manualReferenceId,
        salesforceAccountId,
        salesforceAccountName,
        customTargetFile,
        customMutation,
        addToBillingHold,
        clearInheritedEvidence,
      },
    });
    if (!payload) return;
    const prefill = draft.prefillAction || null;
    onSave(mergeVarianceActionPrefill({
      payload,
      prefill,
      preferPayloadEvidence: Boolean(selectedEvidence || clearInheritedEvidence),
    }));
  };

  const handleSelectEvidence = (option) => {
    setSelectedEvidence(option);
    setClearInheritedEvidence(false);
    setManualOrderItemId(option.orderItemId || '');
    setManualReferenceId(option.referenceId || '');
    const evidenceAccountId = option.salesforceAccountId
      || option.row?.salesforceAccountId
      || option.row?.accountId
      || option.row?.accountMatch?.salesforceAccountId
      || '';
    const evidenceAccountName = option.salesforceAccountName
      || option.row?.salesforceAccountName
      || option.row?.accountName
      || option.row?.dealerName
      || option.row?.accountMatch?.salesforceAccountName
      || '';
    const matchedAccount = accountOptions.find((account) => (
      (evidenceAccountId && account.salesforceAccountId === evidenceAccountId)
      || (evidenceAccountName && account.salesforceAccountName === evidenceAccountName)
    ));
    const preferredAccount = preferredAccountForProduct({
      productCode: context?.productCode,
      accountOptions,
      currentAccountId: matchedAccount?.salesforceAccountId || evidenceAccountId,
      currentAccountName: matchedAccount?.salesforceAccountName || evidenceAccountName,
    });
    const shouldBlockChildDefault = isPopcornParentProductCode(context?.productCode);
    setSalesforceAccountId(preferredAccount?.salesforceAccountId || (shouldBlockChildDefault ? '' : matchedAccount?.salesforceAccountId || evidenceAccountId));
    setSalesforceAccountName(preferredAccount?.salesforceAccountName || (shouldBlockChildDefault ? '' : matchedAccount?.salesforceAccountName || evidenceAccountName));
    setManualAccountSelected(false);
    if (actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT) {
      if (option.productCode) {
        setReplacementProductCode(option.productCode);
        setSelectedCatalogItem(catalogRows.find((item) => normalizeCode(item.productCode) === normalizeCode(option.productCode)) || null);
      }
      if (option.price || option.price === 0) setProposedPrice(option.price);
    } else if (actionType === VARIANCE_ACTIONS.CHANGE_PRICE) {
      if (option.price || option.price === 0) setProposedPrice(option.price);
    } else if (actionType === VARIANCE_ACTIONS.CUSTOM) {
      if (option.productCode) setCustomProductCode(option.productCode);
      if (option.price || option.price === 0) setCustomPrice(option.price);
    }
  };

  const handleReplacementCatalogChange = (_event, option) => {
    const nextCode = typeof option === 'string' ? option : option?.productCode || '';
    setSelectedCatalogItem(typeof option === 'object' ? option : null);
    setReplacementProductCode(nextCode);
    if (option && typeof option === 'object') {
      const nextPrice = catalogPrice(option);
      if (nextPrice || nextPrice === 0) setProposedPrice(nextPrice);
    }
  };

  const handleAccountChange = (event) => {
    const next = accountOptions.find((account) => account.id === event.target.value);
    setSalesforceAccountId(next?.salesforceAccountId || event.target.value || '');
    setSalesforceAccountName(next?.salesforceAccountName || '');
    setManualAccountSelected(true);
  };

  const handleClearInheritedEvidence = () => {
    setSelectedEvidence(null);
    setManualOrderItemId('');
    setManualReferenceId('');
    setSalesforceAccountId('');
    setSalesforceAccountName('');
    setManualAccountSelected(false);
    setClearInheritedEvidence(true);
  };

  return (
    <Dialog open={Boolean(draft)} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        {VARIANCE_ACTION_LABELS[actionType] || 'Variance action'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Alert severity="info">
            Drafting a product variance from {context?.sourceLabel || 'source evidence'}.
          </Alert>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              Product
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 800 }}>
              {productSummary}
            </Typography>
          </Box>
          <TextField
            label="Problem"
            value={problem}
            onChange={(event) => setProblem(event.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          <TextField
            label="Fix"
            value={fix}
            onChange={(event) => setFix(event.target.value)}
            multiline
            minRows={2}
            fullWidth
          />
          {actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
              <Autocomplete
                freeSolo
                options={replacementCatalogRows}
                value={selectedCatalogItem || replacementProductCode}
                inputValue={replacementProductCode}
                getOptionLabel={catalogOptionLabel}
                onChange={handleReplacementCatalogChange}
                onInputChange={(_event, value, reason) => {
                  if (reason === 'input') {
                    setReplacementProductCode(value);
                    setSelectedCatalogItem(null);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Replacement product code"
                    helperText={showAllCatalogProducts
                      ? 'Showing all catalog products'
                      : `Filtered to ${contextProductFamily === 'UNCODED' ? 'matching' : contextProductFamily} product family`}
                  />
                )}
                fullWidth
              />
              <TextField
                label="Replacement price"
                type="number"
                value={proposedPrice}
                onChange={(event) => setProposedPrice(event.target.value)}
                fullWidth
              />
            </Stack>
          )}
          {actionType === VARIANCE_ACTIONS.REPLACE_PRODUCT && contextProductFamily !== 'UNCODED' && (
            <Button
              size="small"
              variant={showAllCatalogProducts ? 'contained' : 'outlined'}
              onClick={() => setShowAllCatalogProducts((value) => !value)}
              sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
            >
              {showAllCatalogProducts ? 'Filter to base code' : 'Show all products'}
            </Button>
          )}
          {actionType === VARIANCE_ACTIONS.CHANGE_PRICE && (
            <TextField
              label="Proposed price"
              type="number"
              value={proposedPrice}
              onChange={(event) => setProposedPrice(event.target.value)}
              fullWidth
            />
          )}
          {actionType === VARIANCE_ACTIONS.CUSTOM && (
            <Stack spacing={1}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <FormControl fullWidth size="small">
                  <InputLabel id="custom-target-file-label">Target file</InputLabel>
                  <Select
                    labelId="custom-target-file-label"
                    label="Target file"
                    value={customTargetFile}
                    onChange={(event) => setCustomTargetFile(event.target.value)}
                  >
                    <MenuItem value="gm">GM file</MenuItem>
                    <MenuItem value="cpq">CPQ / PeopleSoft</MenuItem>
                  </Select>
                </FormControl>
                <FormControl fullWidth size="small">
                  <InputLabel id="custom-mutation-label">Output action</InputLabel>
                  <Select
                    labelId="custom-mutation-label"
                    label="Output action"
                    value={customMutation}
                    onChange={(event) => setCustomMutation(event.target.value)}
                  >
                    <MenuItem value="add">Add row</MenuItem>
                    <MenuItem value="remove">Remove / zero row</MenuItem>
                    <MenuItem value="adjust">Adjust price</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label="Output product code"
                  value={customProductCode}
                  onChange={(event) => setCustomProductCode(event.target.value)}
                  fullWidth
                />
                <TextField
                  label={customMutation === 'remove' ? 'Current price' : 'Output price'}
                  type="number"
                  value={customPrice}
                  onChange={(event) => setCustomPrice(event.target.value)}
                  fullWidth
                />
              </Stack>
              <Alert severity="info">
                Custom variances still compile to add, remove, or adjust so exports stay auditable.
              </Alert>
            </Stack>
          )}
          {actionType === VARIANCE_ACTIONS.REMOVE_FROM_CPQ && (
            <FormControlLabel
              control={<Checkbox checked={addToBillingHold} onChange={(event) => setAddToBillingHold(event.target.checked)} />}
              label="Add to Billing Hold"
            />
          )}
          {showAccountPicker && (
            <FormControl fullWidth size="small">
              <InputLabel id="variance-action-account-label">Salesforce account</InputLabel>
              <Select
                labelId="variance-action-account-label"
                label="Salesforce account"
                value={selectedAccountOption?.id || ''}
                onChange={handleAccountChange}
              >
                {accountOptions.map((account) => (
                  <MenuItem key={account.id} value={account.id}>{account.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {requiresManualAccount && (
            <Alert severity="warning">
              Multiple Salesforce accounts are available for this BAC. Pick the account before saving this product action.
            </Alert>
          )}
          {salesforceAccountId && !requiresManualAccount && (
            <Alert severity="success" icon={false}>
              Salesforce account: {salesforceAccountName || 'Selected account'}{salesforceAccountId ? ` (${salesforceAccountId})` : ''}
            </Alert>
          )}
          {(draft?.prefillAction || canClearInheritedEvidence) && (
            <Stack spacing={0.75} sx={{ alignItems: 'flex-start' }}>
              <Button
                size="small"
                variant={clearInheritedEvidence ? 'contained' : 'outlined'}
                onClick={handleClearInheritedEvidence}
                sx={{ textTransform: 'none' }}
              >Undo automatic inheritance</Button>
              {canClearInheritedEvidence && (
                <Typography variant="caption" color="text.secondary">
                  Use original CPQ row {inheritedCpqOverride.originalProductCode} instead of inherited evidence {inheritedCpqOverride.inheritedProductCode}.
                </Typography>
              )}
            </Stack>
          )}
          <VarianceEvidencePicker
            gmItems={gmItems}
            cpqItems={cpqItems}
            dpeItems={dpeItems}
            appsheetData={appsheetData}
            selectedEvidence={selectedEvidence}
            currentOrderItemId={manualOrderItemId}
            currentReferenceId={manualReferenceId}
            currentProductCode={replacementProductCode || customProductCode || context?.productCode || ''}
            onSelectEvidence={handleSelectEvidence}
          />
          <Stack spacing={0.75}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700 }}>
              Note chips
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {productNoteTemplates.map((template) => (
                <Chip
                  key={template.id || template.key || template.label}
                  size="small"
                  color="info"
                  variant="outlined"
                  clickable
                  label={template.label}
                  onClick={() => setNote(template.text || '')}
                />
              ))}
            </Stack>
          </Stack>
          <TextField
            label="Product investigation note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
            multiline
            minRows={3}
            helperText="Stamped with product context, selected evidence, effective date, and author."
            fullWidth
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          Add to audit
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VarianceActionDialog;
